<template>
  <v-container fluid class='pa-6'>
    <v-row>
      <v-col class='d-flex align-items-center pb-6'>
        <div>
          <h1 class='mb-0' style='line-height: 1;'>Live orders</h1>
          <div class='text-caption mt-2'>Updated at: {{format_date_with_seconds(updated_at)}}</div>
        </div>
        <div class='ml-auto'>
          <a v-if='pending_carter_count > 0' href='https://app.usecarter.com/orders' target='blank' style='color: #7642f4;'>
            {{pending_carter_count}} Orders require scheduling
          </a>
        </div>
        <div class='ml-2'>
          <div class='btn-group'>
            <button @click='show_filter = !show_filter' class='btn' :class='show_filter ? "btn-secondary" : "btn-outline-secondary"'>
              Filters
              <i class='mdi mdi-filter'></i>
            </button>
            <button v-if='has_active_filters' @click='clear_filters' class='btn btn-outline-danger'>
              <i class='mdi mdi-filter-remove'></i>
            </button>
          </div>
          <button v-if='feature_stokvel()' @click='show_stokvel = !show_stokvel' class='btn' :class='show_stokvel ? "btn-secondary" : "btn-outline-secondary"'>
            Stokvel
            <i class='mdi mdi-calendar'></i>
          </button>
        </div>
      </v-col>
    </v-row>

    <v-row>
      <v-col v-if='show_filter' cols='12'>
        <v-card>
          <orders-filter></orders-filter>
        </v-card>
      </v-col>
      <v-col v-if='show_stokvel' cols=12 class='pt-0'>
        <v-card class='pa-3'>
          <v-form ref='stokvel_export' target='_top' method='get' @submit='validate_stokvel_export'>
            <div class='text-center'>Click here to download stokvel orders for the next 3 days</div>
            <div class='d-flex align-items-center'>
              <v-select name='supplier_id' v-model='stokvel_export_supplier' :items='suppliers_options' :rules='[rules.required]' label='Select a branch'></v-select>
              <v-btn type='submit' :disabled='loading_suppliers' class='primary ml-4'>
                <v-icon>mdi-download</v-icon>
                Download sheet
              </v-btn>
            </div>
          </v-form>
        </v-card>
      </v-col>
    </v-row>

    <v-row>
      <v-col cols='12'>
        <v-card class='pb-8'>
          <v-data-table hide-default-footer class='mb-8'
            :loading='loading'
            :headers='headers'
            :items='orders'
            :server-items-length='orders_count'
            @click:row="on_row_click"
            >
            <template v-slot:item.created_at="{ item }">
              {{ format_date(item.created_at) }}
            </template>
            <template v-slot:item.collection_date="{ item }">
              {{ item.dispatch_method == 'manual_delivery' ? 'Manual delivery' : item.dispatch_method == 'picup_manual_price' ? item.shipping_provider_option : format_date(item.collection_date) }}
              <a v-if='item.dispatch_action_required' :href='item.dispatch_action_url' target="_blank" @click.stop='' class='btn btn-primary text-white btn-sm'>
                <i class='mdi mdi-truck-delivery' />
                Dispatch order
              </a>
              <template v-else-if="item.pending_job">
                <a v-if='item.pending_job.waybill_download_url' :href="item.pending_job.waybill_download_url" @click.stop='' target="_blank" class='btn btn-primary text-white btn-sm'>
                  <i class='mdi mdi-file-document' />
                  Access waybill
                </a>
              </template>
            </template>
            <template v-slot:item.shipping_status="{ item }">
              <v-chip v-if='item.pending_job' :color="get_job_color(item.pending_job.status, item.pending_job.cold_count)" :dark="get_job_color(item.pending_job.status, item.pending_job.cold_count) != 'yellow'" small>
                {{ formatted_job_type(item.pending_job.job_type) }}: {{ item.pending_job.formatted_status }}
              </v-chip>
              <v-chip v-else-if='item.shipping_status == "pending"'> Scheduled </v-chip>
            </template>
            <template v-slot:item.stokvel="{ item }">
              {{ item.stokvel ? 'Stokvel' : 'Retail' }}
            </template>
            <template v-slot:item.total_in_cents="{ item }">
              <div class='text-right'>
                {{ number_to_currency(item.total_in_cents) }}
              </div>
            </template>
          </v-data-table>
          <v-pagination
            v-model="current_page"
            :length="pages"
            :total-visible='9'
          ></v-pagination>
        </v-card>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import { mapState } from 'vuex'
import { DateTime } from 'luxon'
import orders_filter from '../orders_filter'
import base_service from '../../services/base'
import currency from 'currency.js'
import app_config from '../../application.yml'

export default {
  components: {
    'orders-filter': orders_filter
  },

  data() {
    return {
      headers: [
        { text: 'Placed at', value: 'created_at' },
        { text: 'Dispatch at', value: 'collection_date' },
        { text: 'Shipping status', value: 'shipping_status' },
        { text: 'Branch', value: 'supplier_name' },
        { text: 'Ref', value: 'reference' },
        { text: 'Size', value: 'stokvel' },
        { text: 'Total', value: 'total_in_cents' }
      ],
      show_filter: false,
      show_stokvel: false,
      suppliers_options: [],
      loading_suppliers: false,
      stokvel_export_supplier: null,
      rules: {
        required: (value) => Boolean(value) || 'Required.'
      }
    }
  },

  created() {
    this.$store.dispatch('orders/start_polling')
    this.fetch_orders()
    this.fetch_suppliers()
  },

  beforeDestroy() {
    this.$store.dispatch('orders/stop_polling')
  },

  watch: {
    '$route.query': {
      handler() {
        this.fetch_orders()
      },
      deep: true
    }
  },

  computed: {
    ...mapState({
      orders: (state) => state.orders.all,
      pagination: (state) => state.orders.pagination,
      loading: (state) => state.orders.loading,
      updated_at: (state) => state.orders.updated_at,
      pending_carter_count: (state) => state.orders.pending_carter_count
    }),

    orders_count() {
      return this.pagination?.count || 0
    },

    pages() {
      return this.pagination?.pages || 1
    },

    current_page: {
      get() {
        return parseInt(this.$route.query.page) || 1
      },
      set(page) {
        const query = { ...this.$route.query, page }
        if (page === 1) delete query.page
        this.$router.replace({ query }).catch(() => {})
      }
    },

    has_active_filters() {
      const query = this.$route.query
      return Boolean(
        query.q || query.type || query.suppliers || query.status ||
        query.total_min || query.total_max || query.from || query.to
      )
    }
  },

  methods: {
    feature_stokvel() {
      return Boolean(app_config.features.stokvel)
    },

    build_api_params() {
      const query = this.$route.query
      return {
        page: query.page || 1,
        per_page: 20,
        desc: true,
        q: query.q || '',
        type: query.type || '',
        suppliers: query.suppliers || '',
        status: query.status || '',
        total_min: query.total_min || '',
        total_max: query.total_max || '',
        from: query.from || '',
        to: query.to || ''
      }
    },

    fetch_orders() {
      const params = this.build_api_params()
      this.$store.dispatch('orders/fetch', params)
    },

    fetch_suppliers() {
      this.loading_suppliers = true
      this.$store.dispatch('suppliers/fetch')
        .then((json) => {
          this.suppliers_options = json.map((supplier) => ({ value: supplier.id, text: supplier.name }))
          this.loading_suppliers = false
        })
        .catch((error) => {
          this.loading_suppliers = false
        })
    },

    format_date(time) {
      return DateTime.fromISO(time).toFormat('ff')
    },

    format_date_with_seconds(time) {
      return DateTime.fromISO(time).toFormat('FF')
    },

    get_job_color(job_status, cold_count = 0) {
      if (job_status === 'pending') return 'red'
      if (job_status === 'pending_loyalty_card') return 'green'
      if (job_status === 'issued') return 'yellow'
      if (job_status === 'stock_short') return 'orange'
      if (cold_count >= 1 && ['pending_payment_at_collection', 'pending_collection'].includes(job_status)) return 'orange'
      if (['pending_payment_at_collection', 'pending_collection', 'pending_delivery'].includes(job_status)) return 'green'
      if (job_status === 'cold') return 'blue'
      if (['delivered', 'collected'].includes(job_status)) return 'green'
    },

    formatted_job_type(job_type) {
      if (job_type === 'supplier_order_pack') return 'Picker'
      if (job_type === 'supplier_order_process') return 'Cashier'
    },

    number_to_currency(int) {
      return currency(int, { fromCents: true }).format({ symbol: 'R ' })
    },

    on_row_click(row) {
      const order = JSON.parse(JSON.stringify(row))
      this.$store.dispatch('order/set', order)
        .then(() => this.$router.push(`/orders/${order.id}`))
    },

    clear_filters() {
      this.$router.replace({ query: {} }).catch(() => {})
    },

    validate_stokvel_export(event) {
      event.preventDefault()
      if (!this.$refs.stokvel_export.validate()) return

      base_service.download('/orders/stokvel_export.pdf', { supplier_id: this.stokvel_export_supplier })
        .then((response) => response.blob())
        .then((blob) => {
          const url = window.URL.createObjectURL(blob)

          const a = document.createElement('a')
          a.style.display = 'none'
          a.href = url
          const supplier = this.suppliers_options.find((s) => s.value === this.stokvel_export_supplier)
          a.download = `Stokvel stock - ${supplier.text} - ${DateTime.local().toLocaleString(DateTime.DATE_MED)}.pdf`
          document.body.appendChild(a)
          a.click()
          window.URL.revokeObjectURL(url)
          document.body.removeChild(a)
        })
    }
  }
}
</script>
